import Alpine from 'alpinejs'
import collapse from '@alpinejs/collapse'
import resize from '@alpinejs/resize'
 
import { lenis } from './lenis'

const tracking = {
	addToCart(item, quantity) {
		if (typeof gtag === 'undefined') return;
		
		gtag('event', 'add_to_cart', {
			currency: 'GBP',
			value: (item.price / 100) * quantity,
			items: [{
				item_id: String(item.product_id),
				item_name: item.product_title,
				item_variant: item.variant_title || item.title,
				price: item.price / 100,
				quantity: quantity
			}]
		});
	},

	removeFromCart(item) {
		if (typeof gtag === 'undefined') return;
		
		gtag('event', 'remove_from_cart', {
			currency: 'GBP',
			value: item.price / 100 * item.quantity,
			items: [{
				item_id: String(item.product_id),
				item_name: item.product_title,
				item_variant: item.variant_title || item.title,
				price: item.price / 100,
				quantity: item.quantity
			}]
		});
	},

	viewCart(cart) {
		if (typeof gtag === 'undefined') return;
		
		gtag('event', 'view_cart', {
			currency: 'GBP',
			value: cart.total_price / 100,
			items: cart.items.map(item => ({
				item_id: String(item.product_id),
				item_name: item.product_title,
				item_variant: item.variant_title || item.title,
				price: item.price / 100,
				quantity: item.quantity
			}))
		});
	},

	beginCheckout(cart) {
		if (typeof gtag === 'undefined') return;
		
		gtag('event', 'begin_checkout', {
			currency: 'GBP',
			value: cart.total_price / 100,
			items: cart.items.map(item => ({
				item_id: String(item.product_id),
				item_name: item.product_title,
				item_variant: item.variant_title || item.title,
				price: item.price / 100,
				quantity: item.quantity
			}))
		});

		// Store checkout data for abandonment tracking
		localStorage.setItem('checkout_started', JSON.stringify({
			timestamp: Date.now(),
			value: cart.total_price / 100,
			currency: 'GBP'
		}));
	}
}

export const initAlpine = () => {
	window.Alpine = Alpine

	Alpine.store('cart', {
		isCartOpen: false,
		loading: false,
		updating: false,
		items: [],
		itemCount: 0,
		totalPrice: 0,

		async init() {
			await this.fetchCart()
		},

		toggleDrawer() {
			this.isCartOpen = !this.isCartOpen
			if (this.isCartOpen) {
				lenis.stop()
				this.fetchCart()
			}
		},

		openDrawer() {
			console.log('stop')
			this.isCartOpen = true
			this.fetchCart()

			if (this.items.length > 0) {
				tracking.viewCart({
					items: this.items,
					total_price: this.totalPrice
				})
			}
		},

		closeDrawer() {
			lenis.start()
			this.isCartOpen = false
		},

		async fetchCart() {
			this.loading = true
			try {
				const response = await fetch('/cart.js')
				const cart = await response.json()
				this.updateCartData(cart)
			} catch (error) {
				console.error('Error fetching cart:', error)
			} finally {
				this.loading = false
			}
		},

		async addItem(form) {
			this.updating = true
			try {
				const formData = new FormData(form)
				const response = await fetch('/cart/add.js', {
					method: 'POST',
					body: formData
				})

				if (response.ok) {
					const products = await response.json()
					const quantity = parseInt(formData.get('quantity') || 1)

					if (Array.isArray(products.items)) {
						products.items.forEach(item => {
							tracking.addToCart(item, item.quantity)
						})
					} else {
						tracking.addToCart(products, quantity)
					}

					await this.fetchCart()
					this.openDrawer()
				} else {
					const error = await response.json()
					alert(error.message || 'Error adding item to cart')
				}
			} catch (error) {
				console.error('Error adding item:', error)
				alert('Error adding item to cart')
			} finally {
				this.updating = false
			}
		},

		async updateQuantity(key, quantity) {
			if (quantity < 0) return

			const updatedItem = this.items.find(item => item.key === key)
			const previousQuantity = updatedItem ? updatedItem.quantity : 0

			this.updating = true
			try {
				const response = await fetch('/cart/change.js', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						id: key,
						quantity: quantity
					})
				})

				if (response.ok) {
					const cart = await response.json()

					if (quantity === 0 && updatedItem) {
						tracking.removeFromCart(updatedItem)
					} else if (quantity > previousQuantity && updatedItem) {
						const quantityAdded = quantity - previousQuantity
						tracking.addToCart(updatedItem, quantityAdded)
					} else if (quantity < previousQuantity && updatedItem) {
						const quantityRemoved = previousQuantity - quantity
						const removedItem = { ...updatedItem, quantity: quantityRemoved }
						tracking.removeFromCart(removedItem)
					}
					
					this.updateCartData(cart)
				} else {
					console.error('Error updating quantity')
				}
			} catch (error) {
				console.error('Error updating quantity:', error)
			} finally {
				this.updating = false
			}
		},

		async removeItem(key) {
			await this.updateQuantity(key, 0)
		},

		updateCartData(cart) {
			this.items = cart.items
			this.itemCount = cart.item_count
			this.totalPrice = cart.total_price
		},

		formatMoney(price) {
			return new Intl.NumberFormat('en-GB', {
				style: 'currency',
				currency: 'GBP'
			}).format(price / 100)
		},

		proceedToCheckout() {
			tracking.beginCheckout({
				items: this.items,
				total_price: this.totalPrice
			})
			window.location.href = '/checkout'
		}
	})

	Alpine.store('search', {
		query: '',
		results: [],
		loading: false,
		showResults: false,
		isSearchOpen: false,

		search() {
			if (this.query.length < 2) {
				this.results = []
				this.showResults = false
				return
			}

			this.loading = true
			this.showResults = true

			clearTimeout(this.searchTimeout)
			this.searchTimeout = setTimeout(() => {
				this.performSearch()
			}, 500)
		},

		closeResults() {
			console.log('closeResults')
			this.showResults = false
			this.results = []
			this.query = ''
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout)
			}
		},

		async performSearch() {
			try {
				const response = await fetch(
					`/search/suggest.json?q=${encodeURIComponent(
						this.query
					)}&resources[type]=product&resources[limit]=10`
				)
				const data = await response.json()

				this.results = data.resources.results.products.map((product) => ({
					id: product.id,
					title: product.title,
					url: product.url,
					price: this.formatPrice(product.price),
					featured_image: product.featured_image || ''
				}))
			} catch (error) {
				console.error('Search error:', error)
				this.results = []
			} finally {
				this.loading = false
			}
		},

		formatPrice(price) {
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD'
			}).format(price / 100)
		},

		toggleSearch() {
			this.isSearchOpen = !this.isSearchOpen
		},

		openSearch() {
			this.isSearchOpen = true
		},

		closeSearch() {
			this.isSearchOpen = false
		},

		trackingAddToCart(product, quantity) {
			gtag('event', 'add_to_cart', {
				currency: 'GBP',
				value: (product.price / 100) * quantity,
				items: [{
					item_id: product.id,
					item_name: product.title,
					item_variant: product.variant_title,
					price: product.price / 100,
					quantity: quantity
				}]
			});
		}
	})

	Alpine.plugin(resize)
	Alpine.plugin(collapse)
	Alpine.start()
}

export default () => {}